/**
 * Host-approval join requests at `events/{eventId}/joinRequests/{uid}`.
 *
 * Pending requesters are not participants. Only the host can approve, and
 * approval is what appends the uid onto `participants`.
 */
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  runTransaction,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { sanitizeParticipantPhotoURL } from '../utils/authIdentity';
import { findParticipant, reconcileOwnGuestParticipant } from '../utils/identity';
import {
  EVENT_STATUS,
  JOIN_REQUEST_STATUS,
  isEventLive,
} from '../utils/eventStatus';
import { describeError } from '../utils/errorLog';

export { JOIN_REQUEST_STATUS };

export class JoinRequestError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'JoinRequestError';
    this.code = code;
  }
}

export function joinRequestRef(eventId, uid) {
  return doc(db, 'events', eventId, 'joinRequests', uid);
}

export function joinRequestsCollection(eventId) {
  return collection(db, 'events', eventId, 'joinRequests');
}

export function buildJoinRequestPayload({ uid, name, photoURL = null }) {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  if (!uid || !trimmed) return null;

  return {
    uid,
    name: trimmed.slice(0, 64),
    photoURL: sanitizeParticipantPhotoURL(photoURL),
    status: JOIN_REQUEST_STATUS.pending,
    createdAt: Date.now(),
  };
}

export function joinRequestFromSnapshot(snapshot) {
  if (!snapshot?.exists?.()) return null;
  return { id: snapshot.id, ...snapshot.data() };
}

export async function fetchJoinRequest(eventId, uid) {
  if (!eventId || !uid) return null;
  try {
    const snapshot = await getDoc(joinRequestRef(eventId, uid));
    return joinRequestFromSnapshot(snapshot);
  } catch (error) {
    console.error('Failed to read join request:', describeError(error));
    return null;
  }
}

/** Requester listens to their own document. */
export function watchJoinRequest(eventId, uid, onChange) {
  if (!eventId || !uid || typeof onChange !== 'function') return () => {};

  return onSnapshot(joinRequestRef(eventId, uid), (snapshot) => {
    onChange(joinRequestFromSnapshot(snapshot));
  }, (error) => {
    console.error('Join request listener failed:', describeError(error));
  });
}

/** Host listens to the whole subcollection and filters pending client-side. */
export function watchJoinRequests(eventId, onChange) {
  if (!eventId || typeof onChange !== 'function') return () => {};

  return onSnapshot(joinRequestsCollection(eventId), (snapshot) => {
    const docs = snapshot?.docs ?? [];
    onChange(docs.map((entry) => ({ id: entry.id, ...entry.data() })));
  }, (error) => {
    console.error('Join requests listener failed:', describeError(error));
  });
}

export function pendingJoinRequests(requests) {
  return (Array.isArray(requests) ? requests : [])
    .filter((request) => request?.status === JOIN_REQUEST_STATUS.pending);
}

function guestParticipantFromRequest(request, fallback = {}) {
  return {
    id: request.uid,
    name: request.name || fallback.name || 'Guest',
    role: 'guest',
    joinedAt: Date.now(),
    photoURL: sanitizeParticipantPhotoURL(request.photoURL ?? fallback.photoURL),
  };
}

/**
 * Host Allow: append the guest and mark the request approved in one
 * transaction so a participant can never land on the event while the
 * request stays pending (or the reverse).
 *
 * Rejoin: if the uid already has a participant row, reuse it and apply the
 * newly approved display name / photo. A second Allow does not append a
 * duplicate uid.
 */
export async function approveJoinRequest({ eventId, request, hostId }) {
  if (!eventId || !request?.uid) {
    throw new JoinRequestError('invalid', 'Missing join request');
  }

  const eventRef = doc(db, 'events', eventId);
  const requestRef = joinRequestRef(eventId, request.uid);

  return runTransaction(db, async (transaction) => {
    const eventSnap = await transaction.get(eventRef);
    const requestSnap = await transaction.get(requestRef);

    if (!eventSnap?.exists?.()) {
      throw new JoinRequestError('not-found', 'Event not found');
    }
    if (!requestSnap?.exists?.()) {
      throw new JoinRequestError('not-found', 'Join request not found');
    }

    const eventData = eventSnap.data() || {};
    const currentRequest = requestSnap.data() || {};

    if (eventData.hostId !== hostId) {
      throw new JoinRequestError('not-host', 'Only the host can approve join requests');
    }
    if (!isEventLive(eventData)) {
      throw new JoinRequestError('event-ended', 'This party has ended');
    }

    const participants = Array.isArray(eventData.participants) ? [...eventData.participants] : [];
    const existing = findParticipant(participants, request.uid);

    if (currentRequest.status === JOIN_REQUEST_STATUS.denied) {
      throw new JoinRequestError('join-denied', 'This request was already declined');
    }

    if (currentRequest.status !== JOIN_REQUEST_STATUS.pending && !existing) {
      throw new JoinRequestError('invalid', 'Join request is not pending');
    }

    const requestName = currentRequest.name || request.name;
    const requestPhoto = currentRequest.photoURL ?? request.photoURL;
    let nextParticipants = participants;
    let participant = existing || guestParticipantFromRequest(currentRequest, request);

    const eventPatch = {};
    if (!existing) {
      nextParticipants = [...participants, participant];
      eventPatch.participants = nextParticipants;
    } else {
      const reconciled = reconcileOwnGuestParticipant(participants, {
        uid: request.uid,
        hostId: eventData.hostId,
        name: requestName,
        photoURL: requestPhoto,
      });
      if (reconciled) {
        nextParticipants = reconciled;
        participant = findParticipant(reconciled, request.uid) || participant;
        eventPatch.participants = reconciled;
      }
    }
    if (
      Array.isArray(eventData.participantIds)
      && !eventData.participantIds.includes(request.uid)
    ) {
      eventPatch.participantIds = [...eventData.participantIds, request.uid];
    }
    if (
      Array.isArray(eventData.joinedParticipantIds)
      && !eventData.joinedParticipantIds.includes(request.uid)
    ) {
      eventPatch.joinedParticipantIds = [...eventData.joinedParticipantIds, request.uid];
    }
    if (Object.keys(eventPatch).length > 0) {
      transaction.update(eventRef, eventPatch);
    }

    if (currentRequest.status !== JOIN_REQUEST_STATUS.approved) {
      transaction.update(requestRef, {
        status: JOIN_REQUEST_STATUS.approved,
        resolvedAt: Date.now(),
        resolvedBy: hostId,
      });
    }

    return participant;
  });
}

export async function denyJoinRequest({ eventId, uid, hostId }) {
  if (!eventId || !uid) {
    throw new JoinRequestError('invalid', 'Missing join request');
  }

  const eventSnap = await getDoc(doc(db, 'events', eventId));
  if (!eventSnap?.exists?.()) {
    throw new JoinRequestError('not-found', 'Event not found');
  }

  const eventData = eventSnap.data();
  if (eventData.hostId !== hostId) {
    throw new JoinRequestError('not-host', 'Only the host can deny join requests');
  }
  if (!isEventLive(eventData)) {
    throw new JoinRequestError('event-ended', 'This party has ended');
  }

  await updateDoc(joinRequestRef(eventId, uid), {
    status: JOIN_REQUEST_STATUS.denied,
    resolvedAt: Date.now(),
    resolvedBy: hostId,
  });
}

export function eventEndedPayload({ endedBy, endedAt = Date.now() }) {
  return {
    status: EVENT_STATUS.ended,
    endedAt,
    endedBy,
  };
}
